import { ReactElement, MouseEvent } from 'react';

import clsx from 'classnames';
import { PickersDay, PickersDayProps } from '@mui/x-date-pickers/PickersDay';

import { Tooltip } from '../../Tooltip';
import { Box } from '../../Box';
import { useIsDesktop } from '../../hooks';

import useStyles from './Day.styles';

/**
 * Свойства компонента {@link Day}
 */
export interface DayProps extends Omit<PickersDayProps<Date>, 'onMouseLeave'> {
  /**
   * Дополнительный CSS-класс.
   */
  className?: string;

  /**
   * Скрыт ли день.
   */
  hidden?: boolean;

  /**
   * Функция проверки, является ли день праздничным.
   */
  testHoliday?: (day: Date) => boolean;

  /**
   * Функция проверки, является ли день частью выбранного диапазона.
   */
  testIsInRange?: (day: Date) => boolean;

  /**
   * Функция проверки, является ли день началом диапазона.
   */
  testIsStart?: (day: Date) => boolean;

  /**
   * Функция проверки, является ли день концом диапазона.
   */
  testIsEnd?: (day: Date) => boolean;

  /**
   * Обработчик события mouseleave.
   */
  onMouseLeave?: (event: MouseEvent<HTMLButtonElement>, day?: Date) => void;

  /**
   * Функция, которая рендерит тултип.
   */
  renderDayTooltip?: (day: Date | null) => ReactElement | null;

  /**
   * Функция, которая рендерит что-либо под цифрой даты.
   */
  renderDayFooter?: (
    day: Date | null,
    isInRange: boolean,
  ) => ReactElement | null;
}

/**
 * Компонент для отображения дня в календаре.
 *
 * @internal
 * @param day - Дата отображаемого дня.
 * @param today - Является ли день сегодняшним.
 * @param disabled - Отключён ли день.
 * @param hidden - Скрыт ли день.
 * @param selected - Выбран ли день.
 * @param testIsInRange - Функция проверки, является ли день частью выбранного диапазона.
 * @param testIsEnd - Функция проверки, является ли день концом диапазона.
 * @param testIsStart - Функция проверки, является ли день началом диапазона.
 * @param testHoliday - Функция проверки, является ли день праздничным.
 * @param className - Дополнительный CSS-класс.
 * @param onMouseLeave - Обработчик события mouseleave.
 * @param renderDayTooltip - Функция, которая рендерит тултип.
 * @param renderDayFooter - Функция, которая рендерит что-либо под цифрой даты.
 * @returns  Возвращает JSX-элемент представляющий компонент Day.
 *
 * @remarks
 *
 * Ссылка на свойства компонента {@link DayProps}
 */
export const Day = ({
  day,
  today,
  disabled,
  hidden,
  selected,
  testIsInRange,
  testIsEnd,
  testIsStart,
  testHoliday,
  className,
  onMouseLeave,
  renderDayTooltip,
  renderDayFooter,
  ...other
}: DayProps) => {
  const isDesktop = useIsDesktop();

  const { classes } = useStyles();

  const tooltipContent = renderDayTooltip?.(day);

  const isInRange = testIsInRange?.(day);
  const isStart = isInRange && testIsStart?.(day);
  const isEnd = isInRange && testIsEnd?.(day);

  const { outsideCurrentMonth } = other;

  return (
    <Tooltip
      title={tooltipContent || ''}
      placement="bottom"
      disabled={!isDesktop || !tooltipContent || outsideCurrentMonth}
    >
      <div
        className={clsx(
          classes.dayWrapper,
          isInRange && classes.dayWrapperInRange,
          isStart && classes.dayWrapperStart,
          isEnd && classes.dayWrapperEnd,
        )}
      >
        <Box display="flex" flexDirection="column" position="relative">
          <PickersDay
            classes={{
              root: classes.day,
              today: classes.current,
              selected: classes.daySelected,
              disabled: classes.dayDisabled,
            }}
            className={clsx(className, {
              [classes.hidden]: hidden,
              [classes.holiday]: testHoliday?.(day),
              aqa_icon_button: true,
            })}
            day={day}
            today={today}
            selected={selected}
            disabled={disabled}
            hidden={hidden}
            tabIndex={hidden || disabled ? -1 : 0}
            disableRipple
            onMouseLeave={(e) => onMouseLeave?.(e, day)}
            {...other}
          />
          {!outsideCurrentMonth && renderDayFooter?.(day, !!isInRange)}
        </Box>
      </div>
    </Tooltip>
  );
};
